import { UIElement } from '../ui-element';
import { keyCodes, isKeyPressed, TabIndex } from '../../global/keyboard';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { dispatchCustomEvent } from '../../global/events';
import { insertElements } from '../../global/render-api';
import type { NullOr } from '../../global/utils.type';
import type { ElementChildren } from '../ui-element.type';
import styles from './ui-award.css';
import { CmsAttribute } from '../../decorators/cms-attribute.decorator';
import { CMS_VALUE_TYPES, CMS_CONTENT_MODES } from '../../cms';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIAward
 * @element ui-award
 * @classdesc Represents a class for <code>ui-award</code> element.
 * Single ui-award should be child of ui-awards container.
 * @fires event:award-open
 * @property {string} [price] {@attr price} Price of the award.
 * @property {string} [oldPrice] {@attr old-price} Old price (strike-through) price.
 * @property {string} [labelPrice] {@attr label-price} Label of the price.
 * @property {string} [labelPriceExtra] {@attr label-price-extra} More texts to the price container.
 * @property {number} [awardId] {@attr award-id} Award ID specified by back-end usually
 * for reward program in iBank.
 * @property {string} [image] {@attr image} Image URL for the award.
 * @property {string} [alt] {@attr alt} Alternate text for image.
 * @property {boolean} [clickable] {@attr clickable} If true whole ui-award will be clickable
 * and focusable.
 * @property {string} [nolazy] {@attr nolazy} Prevents set 'loading=lazy' attribute for image.
 * loading='lazy | eager' might cause side effect for image rendering.
 * @slot
 * @example
 * <ui-award price="5400" label-price="Points" award-id="11"
 *    image="assets/mocks/images/award-mock-01.jpg">
 *   <ui-badge color="turquoise">Business</ui-badge>
 *   <p>New image size: 600x600 px! Old ones will be scaled up.</p>
 * </ui-award>
 */
@Component({
    tag: 'ui-award',
    styles,
    cms: {
        mode: CMS_CONTENT_MODES.Children,
    },
})
export class UIAward extends UIElement {
    @Attribute(String) price!: string;
    @Attribute(String) oldPrice!: string;
    @Attribute(String) labelPrice!: string;
    @Attribute(String) labelPriceExtra!: string;
    // null is a default value for legacy compatibility.
    @Attribute(Number) awardId: NullOr<number> = null;
    @CmsAttribute({ valueType: CMS_VALUE_TYPES.Url })
    @Attribute(String)
    image!: string;
    @Attribute(String) alt!: string;
    @Attribute(Boolean) clickable!: boolean;
    @Attribute(Boolean) nolazy!: boolean;

    /** If exists link to inner link */
    @Child('a') innerLink!: HTMLAnchorElement;

    private readonly awardOpenHandler = this.handleAwardOpen.bind(
        this
    ) as EventListener;

    /** Synthetic click events forwarded to the inner link or this award. */
    private readonly forwardedClickEvents = new WeakSet<MouseEvent>();

    /**
     * Handles click and keyboard events.
     * @param {KeyboardEvent | MouseEvent} event
     * @private
     */
    private handleAwardOpen(event: KeyboardEvent | MouseEvent): void {
        if (
            event instanceof MouseEvent &&
            this.forwardedClickEvents.has(event)
        ) {
            return;
        }
        const isActivationKey =
            event instanceof KeyboardEvent &&
            (isKeyPressed(event, keyCodes.ENTER) ||
                isKeyPressed(event, keyCodes.SPACE));
        if (!isActivationKey && event.type !== 'click') {
            return;
        }
        // This is if for the legacy support.
        if (!this.clickable) {
            return;
        }
        const target = event.target;
        const isFromInnerLink =
            !!this.innerLink &&
            target instanceof Node &&
            this.innerLink.contains(target);
        if (isActivationKey && isFromInnerLink) {
            return;
        }
        event.preventDefault();
        if (isActivationKey) {
            this.forwardClick();
        }

        const params: Record<string, string | number> = {};
        if (this.awardId || this.awardId === 0) {
            params['award-id'] = this.awardId;
        }
        dispatchCustomEvent(this, 'award-open', params);
    }

    /**
     * Emulates a real user click on the inner link (or on the award itself when there is
     * no link) so that delegated listeners (modal openers, data-event observers) receive it.
     * @private
     */
    private forwardClick(): void {
        const event = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            composed: true,
            view: window,
        });
        this.forwardedClickEvents.add(event);
        (this.innerLink ?? this).dispatchEvent(event);
    }

    /**
     * Gets the first link from the current ui-award.
     * @deprecated use innerLink property instead.
     * @returns {HTMLAnchorElement | null}
     */
    getInnerLink(): NullOr<HTMLAnchorElement> {
        return this.innerLink;
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        this.addEventListener('click', this.awardOpenHandler);
        this.addEventListener('keydown', this.awardOpenHandler);
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        this.removeEventListener('click', this.awardOpenHandler);
        this.removeEventListener('keydown', this.awardOpenHandler);
    }

    /**
     * @inheritDoc
     */
    render(): void {
        if (!this.image) {
            console.warn(
                `${this.constructor.name}: used without image attribute`
            );
        }
        const hasInnerLink = !!this.querySelector('a');
        const priceContainers: ElementChildren = [
            {
                tagName: 'div',
                classList: {
                    'ui-award__price-container': true,
                },
                children: [
                    (this.oldPrice || this.price) && {
                        tagName: 'div',
                        children: [
                            this.oldPrice && {
                                tagName: 'div',
                                children: [this.oldPrice],
                                classList: {
                                    'ui-award__old-price': true,
                                },
                            },
                            this.price && {
                                tagName: 'div',
                                children: [this.price],
                                classList: {
                                    'ui-award__price': true,
                                },
                            },
                        ],
                        classList: {
                            'ui-award__price-wrapper': true,
                        },
                    },
                    this.labelPrice && {
                        tagName: 'div',
                        children: [this.labelPrice],
                        classList: {
                            'ui-award__price-extra': true,
                        },
                    },
                    this.labelPriceExtra && {
                        tagName: 'div',
                        children: [this.labelPriceExtra],
                        classList: {
                            'ui-award__price-extra': true,
                        },
                    },
                ],
            },
        ];

        const content: ElementChildren = [].filter
            .call(
                this.childNodes,
                (node: Element) => node.localName !== 'ui-badge'
            )
            .concat(priceContainers as never);

        insertElements(this, [
            {
                tagName: 'figure',
                children: [
                    {
                        tagName: 'img',
                        attributes: {
                            src: this.image || '',
                            alt: this.alt || '',
                            loading: this.nolazy ? null : 'lazy',
                        },
                    },
                ],
            },
            {
                tagName: 'article',
                children: content,
            },
        ]);
        this.clickable =
            this.clickable ||
            !!this.awardId ||
            this.awardId === 0 ||
            !!this.dataset.event ||
            hasInnerLink ||
            !!this.onclick;
        if (this.clickable) {
            this.tabIndex = +TabIndex.Active;
        }
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.subscribeEvents();
    }
}
